import { RefObject, useEffect, useRef } from 'react';

interface UseOnClickOutsideOptions {
  excludeRefs?: Array<RefObject<HTMLElement | null>>;
}

const useOnClickOutside = <TElement extends HTMLElement = HTMLDivElement>(
  handler: (event: Event) => void,
  enabled = true,
  options?: UseOnClickOutsideOptions,
) => {
  const ref = useRef<TElement | null>(null);
  const handlerRef = useRef(handler);
  const excludeRefs = options?.excludeRefs;

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const listener = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!ref.current || ref.current.contains(target)) return;
      if (excludeRefs?.some(excludeRef => excludeRef.current?.contains(target))) return;
      handlerRef.current(event);
    };

    if (enabled) {
      document.addEventListener('pointerdown', listener);
    }

    return () => {
      document.removeEventListener('pointerdown', listener);
    };
  }, [enabled, excludeRefs]);

  return ref;
};

export default useOnClickOutside;
